import { useQuery } from '@tanstack/react-query';
import { Star, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface ReviewRef {
  id: string;
  name: string;
}

interface ReviewDetail {
  id: string;
  rating: number;
  comment?: string;
  car: ReviewRef | null;
  author: ReviewRef | null;
  booking_id: string;
  supplier_id?: string;
  created_at: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-MY');
}

export function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility();
  const canDelete = ability.can('delete', 'Review');

  const { data, isLoading, isError } = useQuery<ReviewDetail>({
    queryKey: RESOURCE_KEYS.review.one(id as string),
    queryFn: () => api.get(`/reviews/${id}`),
  });

  const remove = useConfirmedMutation({
    mutationFn: () => api.delete(`/reviews/${id}`),
    invalidateKey: RESOURCE_KEYS.review.list,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title="Review" fallback="/reviews" />
        <p className="text-steel py-12">Review not found.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Review" fallback="/reviews" />

      <div className="max-w-2xl space-y-4 rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-canvas p-6">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-ink flex items-center gap-1">
            {data.rating}
            <Star size={16} aria-hidden="true" fill="currentColor" />
          </span>
          <span className="text-xs text-steel">{formatDate(data.created_at)}</span>
        </div>

        <p className="whitespace-pre-wrap text-sm text-ink">
          {data.comment || <span className="text-steel italic">No comment</span>}
        </p>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-steel">Car</dt>
          <dd>
            {data.car ? (
              <Link to={`/cars/${data.car.id}/edit`} className="text-primary underline">
                {data.car.name}
              </Link>
            ) : (
              <span className="text-steel italic">Car unavailable</span>
            )}
          </dd>

          <dt className="text-steel">Author</dt>
          <dd>
            {data.author ? (
              <Link to={`/users/${data.author.id}`} className="text-primary underline">
                {data.author.name}
              </Link>
            ) : (
              <span className="text-steel italic">User unavailable</span>
            )}
          </dd>

          <dt className="text-steel">Booking</dt>
          <dd>
            <Link to={`/bookings/${data.booking_id}`} className="text-primary underline">
              Booking {data.booking_id.slice(-8)}
            </Link>
          </dd>
        </dl>

        <div className="pt-2">
          <RowActions
            actions={[
              canDelete && {
                key: 'delete',
                label: 'Delete',
                variant: 'danger' as const,
                confirmMessage: 'Delete this review? This cannot be undone.',
                onClick: async () => {
                  await remove.run();
                  navigate('/reviews');
                },
                icon: <Trash2 className="h-4 w-4" />,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
